import * as Sentry from '@sentry/react';
import { initSentry, markAsHandled } from './lib/sentry';
initSentry();

import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { App as DentexeApp } from './App';
import './index.css';
import { AppProviders } from './app/AppProviders';
import { SentryErrorBoundary } from './app/error-boundary';
import { isBrandPreviewPanelEnabled } from '@/shared/config/brand-preview';
import { BrandPreviewPanel } from '@/shared/ui/dev';
import { toast } from './lib/toast';
import { ApiError } from '@/shared/api/types';
import { registerSessionCleanup } from '@/shared/api/auth-session-handlers';
import { GLOBAL_SEARCH_RECENTS_KEY_PREFIX } from '@/features/search/domain/global-search.types';
import { useQueueStore } from '@/features/queue/infrastructure/queue.store';
import { useOrganizationThemeStore } from '@/features/org/infrastructure/organization-theme.store';

// Re-export path keeps HMR from binding a stale App module that still called
// the removed FeatureRoutes throw stub.
const App = DentexeApp;

const API_ERROR_TOAST_COOLDOWN_MS = 2000;

let lastApiErrorToastAt = 0;

function showApiErrorToast(message: string): void {
  const now = Date.now();
  if (now - lastApiErrorToastAt < API_ERROR_TOAST_COOLDOWN_MS) {
    return;
  }

  lastApiErrorToastAt = now;
  toast.failure(message);
}

function handleGlobalApiError(error: Error): void {
  if (error instanceof ApiError && (error.status >= 400 && error.status < 500)) {
    markAsHandled(error);
    if (!error.message.includes('Feature not enabled')) {
      showApiErrorToast(error.message);
    }
    return;
  }

  if (error instanceof ApiError) {
    Sentry.captureException(error);
    showApiErrorToast(error.message);
    return;
  }

  Sentry.captureException(error);
  showApiErrorToast('Something went wrong');
}

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error) => {
      handleGlobalApiError(error as Error);
    },
  }),
  mutationCache: new MutationCache({
    onError: (error) => {
      handleGlobalApiError(error as Error);
    },
  }),
});

registerSessionCleanup(() => {
  queryClient.clear();
  useQueueStore.getState().reset();
  useOrganizationThemeStore.getState().reset();
  const keysToRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i += 1) {
    const key = localStorage.key(i);
    if (key?.startsWith(GLOBAL_SEARCH_RECENTS_KEY_PREFIX)) {
      keysToRemove.push(key);
    }
  }
  for (const key of keysToRemove) {
    localStorage.removeItem(key);
  }
});

const rootElement = document.getElementById('root');
if (!rootElement) throw new Error('Root element #root not found');

ReactDOM.createRoot(rootElement).render(
  <React.StrictMode>
    <SentryErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AppProviders>
          <BrowserRouter>
            <App />
            {isBrandPreviewPanelEnabled() ? <BrandPreviewPanel /> : null}
          </BrowserRouter>
        </AppProviders>
      </QueryClientProvider>
    </SentryErrorBoundary>
  </React.StrictMode>,
);
